// MetaMask provider resolution.
//
// A bare `window.ethereum.isMetaMask` check false-negatives in two real cases:
// (a) the extension injects asynchronously, so an early check reads "absent"
//     for a wallet that is installed (a refresh "fixes" it), and
// (b) with several wallet extensions installed another wallet may own
//     `window.ethereum` while MetaMask is only reachable through
//     `window.ethereum.providers[]`.
// These helpers are the single place that resolves the MetaMask provider so
// requests, event listeners, and network switches all target the same object.

// Returns the MetaMask provider object, or null when none is resolvable.
// Prefers `window.ethereum.providers[]` (multi-wallet) over `window.ethereum`
// itself, and is safe to call during SSR.
export const getMetaMaskProvider = () => {
  if (typeof window === "undefined") return null;
  const { ethereum } = window;
  if (!ethereum) return null;
  if (Array.isArray(ethereum.providers)) {
    const metaMask = ethereum.providers.find((p) => p?.isMetaMask);
    if (metaMask) return metaMask;
  }
  return ethereum.isMetaMask ? ethereum : null;
};

// True when some injected wallet exists but MetaMask is not resolvable —
// distinguishes "another wallet is active" from "MetaMask not installed"
// when choosing an error message.
export const hasNonMetaMaskProvider = () =>
  typeof window !== "undefined" &&
  Boolean(window.ethereum) &&
  !getMetaMaskProvider();

// Waits out MetaMask's async injection before declaring it absent. Resolves
// with the provider as soon as it appears (via the `ethereum#initialized`
// event or a short poll — the event doesn't fire in every browser/wallet
// combination) or with null after `timeoutMs`.
export const waitForMetaMaskProvider = (timeoutMs = 2000) => {
  const existing = getMetaMaskProvider();
  if (existing || typeof window === "undefined") {
    return Promise.resolve(existing);
  }

  return new Promise((resolve) => {
    let settled = false;
    let pollId = null;
    let timeoutId = null;

    const finish = (provider) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("ethereum#initialized", onInitialized);
      clearInterval(pollId);
      clearTimeout(timeoutId);
      resolve(provider);
    };

    // Another wallet can fire this event too, so only settle when the
    // resolved provider really is MetaMask; otherwise keep waiting.
    const onInitialized = () => {
      const provider = getMetaMaskProvider();
      if (provider) finish(provider);
    };

    window.addEventListener("ethereum#initialized", onInitialized);
    pollId = setInterval(onInitialized, 200);
    timeoutId = setTimeout(() => finish(getMetaMaskProvider()), timeoutMs);
  });
};
